import { IWebcamItem } from 'api/region/types';
import { IResortPathInfo } from 'api/resort/types';
import { NextLink } from 'components/blocks/NextLink';
import { Routes } from 'routes';
import classNames from 'classnames';
import { useCallback, useRef } from 'react';
import { useVisibilityWindow } from 'util/useVisibilityWindow';
import { BLUR_CONTENT_STYLE_NAME } from 'theme';
// import { useIsMobile } from 'util/useIsMobile';
import { SnowUnit } from 'components/blocks/Unit';
import { formatAgoTimestampOrString, formatAsTodYestTomExact } from 'util/formatAgo';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import styles from './styles.module.scss';
import { FeaturedCamera } from '../FeaturedCamera';

export type IPropsWebcamInfo = {
  blur?: boolean;
  showSnowReport?: boolean;
  showSummitDepth?: boolean;
  cam?: IWebcamItem & { resort?: IResortPathInfo },
  resort?: IResortPathInfo,
  regionName?: string,
  title?: string,
  subtitle?: string,
  onClick?: (cam?: IWebcamItem & { resort?: IResortPathInfo })=>void,
  titleText?: string,
  isCurrent?: boolean,
  imageLink?: (r: IResortPathInfo)=>string,
  titleLink?: (r: IResortPathInfo)=>string,
  startAllLiveOnMobile?:boolean,
  isResortCams?: boolean,
};

export const WebcamInfo = (
  {
    blur,
    cam,
    resort,
    regionName,
    title,
    onClick,
    titleText,
    isCurrent,
    showSnowReport,
    showSummitDepth,
    imageLink = Routes.resortCams,
    titleLink = Routes.resortCams,
    startAllLiveOnMobile,
    isResortCams = false,
  }: IPropsWebcamInfo,
) => {
  if (!title && !resort) {
    // eslint-disable-next-line no-console
    console.warn('Invalid usage of WebcamInfo, should have title or resort info');
  }
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useVisibilityWindow(ref, true);

  const { t } = useOTSTranslation('common');

  const snowReport = (showSnowReport && cam?.snow?.snow) ? (
    <figcaption>
      <SnowUnit values={cam.snow?.snow} />
          &nbsp;
      {formatAsTodYestTomExact(cam.snow?.date, t)}
    </figcaption>
  ) : null;

  const summitDepth = (showSummitDepth && cam?.summitSnowDepth) ? (
    <figcaption>
      <SnowUnit values={cam?.summitSnowDepth} />
    </figcaption>
  ) : null;

  const thumb = isVisible ? (cam?.thumbnail || cam?.image) : null;

  const style = thumb ? { backgroundImage: `url(${thumb})`, backgroundSize: 'cover' } : { };

  const r = cam?.resort || resort;

  const cameraTitle = title ?? cam?.title;
  const computedRegionName = regionName ?? resort?.region ?? r?.region;
  const shouldShowResortInfo = !!r && !isResortCams;
  const resortDescription = shouldShowResortInfo
    ? [r.title, computedRegionName].filter(Boolean).join(', ')
    : null;
  const updatedTimeAgo = cam?.date ? formatAgoTimestampOrString(cam.date) : null;

  // const isMobile = useIsMobile();

  const shouldHideOnMobile = startAllLiveOnMobile && !isResortCams;
  const getHideOnMobileStyle = (className?: string) => classNames(className, shouldHideOnMobile ? styles.hideOnMobile : undefined);

  const onClickCb = useCallback(() => {
    if (onClick) {
      onClick(cam);
    }
  }, [cam, onClick]);

  const cameraImage = (() => {
    if (r) {
      if (isResortCams) {
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
        return (
          <figure
            style={{ ...style, cursor: 'pointer' }}
            className={getHideOnMobileStyle(styles.linkResort)}
            onClick={onClickCb}
            tabIndex={-1}
            title={cameraTitle}
          />
        );
      }
      return (
        <NextLink href={imageLink(r)} className={getHideOnMobileStyle(styles.linkResort)}>
          <figure style={style} tabIndex={-1} title={r.title || title}>
            {snowReport || summitDepth}
          </figure>
        </NextLink>
      );
    }
    return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      <figure style={style} className={getHideOnMobileStyle()} onClick={onClickCb} role="button" tabIndex={-1} title={title}>
        {snowReport || summitDepth}
      </figure>
    );
  })();

  return (
    <div
      className={classNames(styles.box, blur ? BLUR_CONTENT_STYLE_NAME : '')}
      data-current={isCurrent ? 'true' : undefined}
      title={titleText}
      ref={ref}
    >
      {cameraImage}
      {r && (
        isResortCams ? (
          <div className={getHideOnMobileStyle(classNames(styles.resortCamLink))}>
            {cameraTitle && (
            <span className={classNames('h5', styles.title, styles.resortCamTitle)}>
              {cameraTitle}
            </span>
            )}
            {updatedTimeAgo && (
            <span className={classNames(styles.subtitle, styles.subtitleMobileOnly)}>
              {updatedTimeAgo}
            </span>
            )}
          </div>
        ) : (
          <NextLink
            className={getHideOnMobileStyle(classNames(isResortCams ? styles.resortCamLink : undefined))}
            href={titleLink(r)}
          >
            {isResortCams ? (
              <>
                {cameraTitle && (
                <span className={classNames('h5', styles.title, styles.resortCamTitle)}>
                  {cameraTitle}
                </span>
                )}
                {updatedTimeAgo && (
                <span className={classNames(styles.subtitle, styles.subtitleMobileOnly)}>
                  {updatedTimeAgo}
                </span>
                )}
              </>
            ) : (
              <span>
                <span className={styles.metaRow}>
                  {cameraTitle && (
                    <span className={classNames('h5', styles.title)}>
                      {cameraTitle}
                    </span>
                  )}
                  {updatedTimeAgo && (
                    <span className={styles.subtitle}>
                      {updatedTimeAgo}
                    </span>
                  )}
                </span>
                {resortDescription && (
                  <span className={styles.subtitle}>
                    {resortDescription}
                  </span>
                )}
              </span>
            )}
          </NextLink>
        )
      )}
      {startAllLiveOnMobile && !isResortCams && (
        <FeaturedCamera isFresh cam={cam} hideTitle className={styles.showOnMobile} />
      )}
    </div>
  );
};
